import { type FC, useState } from 'react'
import { Badge, Collapse } from 'react-bootstrap'
import classNames from 'classnames'
import { type IntegrationWorkflow, type IntegrationWorkflowCapabilities, IntegrationWorkflowStatus } from '../../../../../../models/IntegrationWorkflow'
import { type IntegrationWorkflowTarget } from './integrationWorkflow'
import { WorkflowStepTimeline } from './WorkflowStepTimeline'
import styles from './RunningWorkflowsTab.module.scss'

const STATUS_PILLS: Record<IntegrationWorkflowStatus, { label: string; className: string }> = {
  [IntegrationWorkflowStatus.RUNNING]: { label: 'Running', className: 'zocks-bg-color-warning zocks-text-warning-text-200' },
  [IntegrationWorkflowStatus.COMPLETED]: { label: 'Completed', className: 'zocks-bg-color-success-background zocks-text-success' },
}

interface RunningWorkflowRowProps {
  target: IntegrationWorkflowTarget
  workflow: IntegrationWorkflow
  capabilities: IntegrationWorkflowCapabilities
  defaultExpanded: boolean
  onStepCompleted: (workflowId: number) => void
}

const RunningWorkflowRow: FC<RunningWorkflowRowProps> = ({ target, workflow, capabilities, defaultExpanded, onStepCompleted }) => {
  const [isExpanded, setExpanded] = useState(defaultExpanded)
  const statusPill = STATUS_PILLS[workflow.status]
  const stepsId = `integration-workflow-${workflow.id}-steps`

  return (
    <div>
      <button
        type='button'
        className='btn w-100 d-flex align-items-center justify-content-between gap-3 border-0 rounded-0 zocks-px-1.25 py-3'
        aria-expanded={isExpanded}
        aria-controls={stepsId}
        onClick={() => setExpanded((expanded) => !expanded)}
      >
        <span className='d-flex align-items-center gap-2 paragraph-small bold text-start'>
          <i className={classNames('bi', isExpanded ? 'bi-chevron-up' : 'bi-chevron-down')} aria-hidden='true' />
          {workflow.name}
        </span>
        <Badge bg='' pill className={classNames('d-flex align-items-center gap-2 paragraph-extra-small bold', statusPill.className)}>
          <span className={classNames(styles.statusDot, 'rounded-circle')} />
          {statusPill.label}
        </Badge>
      </button>
      <Collapse in={isExpanded}>
        <div id={stepsId}>
          <WorkflowStepTimeline target={target} workflow={workflow} capabilities={capabilities} onStepCompleted={onStepCompleted} />
        </div>
      </Collapse>
    </div>
  )
}

interface RunningWorkflowsTabProps {
  target: IntegrationWorkflowTarget
  running: IntegrationWorkflow[]
  completed: IntegrationWorkflow[]
  capabilities: IntegrationWorkflowCapabilities
  onStepCompleted: (workflowId: number) => void
}

export const RunningWorkflowsTab: FC<RunningWorkflowsTabProps> = ({ target, running, completed, capabilities, onStepCompleted }) => (
  <div className='border zocks-border-color-gray-300 rounded-2 mt-3'>
    {[...running, ...completed].map((workflow, index) => (
      <div key={workflow.id} className={classNames({ 'border-top zocks-border-color-gray-300': index > 0 })}>
        <RunningWorkflowRow target={target} workflow={workflow} capabilities={capabilities} defaultExpanded={index === 0} onStepCompleted={onStepCompleted} />
      </div>
    ))}
  </div>
)
